import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import BrandStorySection from './components/BrandStorySection';
import TeamSection from './components/TeamSection';
import TrustCredentialsSection from './components/TrustCredentialsSection';
import RoyalPromiseSection from './components/RoyalPromiseSection';
import VideoTestimonialSection from './components/VideoTestimonialSection';
import AwardsRecognitionSection from './components/AwardsRecognitionSection';
import CommunityImpactSection from './components/CommunityImpactSection';

const AboutDynastyBrandHeritage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Hello! I'm interested in learning more about Nitin Online Hub's gaming services and would like to speak with a gaming expert."
    );
    window.open(`https://wa.me/+917704070169?text=${message}`, '_blank');
  };

  return (
    <>
      <Helmet>
        {/* SEO improvements: target both "Nitin Online Hub" and "Nitin Online Book" */}
        <title>Official Nitin Online Hub | Nitin Online Book - India's Best Gaming Platform</title>
        <meta 
          name="description" 
          content="Discover the story of Nitin Online Hub and Nitin Online Book, the official online gaming platform in India. Learn about our heritage, values, and commitment to providing secure, fair, and enjoyable experiences. Find Nitin online book login, sign up, and app download info here." 
        />
        <meta name="keywords" content="Nitin Online Hub, Nitin Online Book, Nitin book, Nitin betting book, Nitin online book login, Nitin online book sign up, Nitin online book app download, Nitin online book app, Nitin online book india, Nitin online book download, Nitin BOOK betting, Nitin online book review" />
        <meta property="og:title" content="About Nitin Online Hub - Nitin Online Book | Premium Gaming Platform" />
        <meta property="og:description" content="Discover the story of Nitin Online Hub and Nitin Online Book, India's leading online gaming platform. Learn about our heritage, values, and commitment to providing a secure and enjoyable gaming experience." />
        <meta property="og:image" content="https://nitinonlinehub.in/assets/images/logo.png" />
        <meta property="og:url" content="https://nitinonlinehub.in/about-dynasty-brand-heritage" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Nitin Online Hub" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Nitin Online Hub - Nitin Online Book | Premium Gaming Platform" />
        <meta name="twitter:description" content="Discover the story of Nitin Online Hub and Nitin Online Book, India's leading online gaming platform. Learn about our heritage, values, and commitment to providing a secure and enjoyable gaming experience." />
        <meta name="twitter:image" content="https://nitinonlinehub.in/assets/images/logo.png" />
        <link rel="canonical" href="https://nitinonlinehub.in/about-dynasty-brand-heritage" />

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://nitinonlinehub.in/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "About",
                  "item": "https://nitinonlinehub.in/about-dynasty-brand-heritage"
                }
              ]
            }
          `}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
          {/* Hero Section */}
          <HeroSection onWhatsAppClick={handleWhatsAppClick} />
          
          {/* Brand Story Section */}
          <BrandStorySection />
          
          {/* Team Section */}
          {/* <TeamSection /> */}
          
          {/* Trust Credentials Section */}
          <TrustCredentialsSection />
          
          {/* Royal Promise Section */}
          <RoyalPromiseSection onWhatsAppClick={handleWhatsAppClick} />
          
          {/* Video Testimonial Section */}
          <VideoTestimonialSection onWhatsAppClick={handleWhatsAppClick} />
          
          {/* Awards Recognition Section */}
          <AwardsRecognitionSection />
          
          {/* Community Impact Section */}
          <CommunityImpactSection onWhatsAppClick={handleWhatsAppClick} />
        </main>

        {/* Floating WhatsApp Button */}
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={handleWhatsAppClick}
            className="w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-royal hover:shadow-glow transition-all duration-300 whatsapp-pulse"
            aria-label="Contact us on WhatsApp"
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z"/>
            </svg>
          </button>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              {/* Brand Info */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-royal border-2 border-white">
                    <img
                      src="/assets/images/logo.png" // Adjust path if needed
                      alt="Nitin Logo"
                      className="h-10 w-10 object-contain"
                      style={{ borderRadius: '50%' }}
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-gradient-royal">Nitin</h3>
                    <p className="text-sm text-gray-400">Online Hub</p>
                  </div>
                </div>
                <p className="text-gray-300 mb-4 max-w-md">
                  India's most trusted premium gaming platform, where every player receives royal treatment and personalized WhatsApp support.
                </p>
                <div className="flex space-x-4">
                  {/* Instagram */}
                  <a
                    href="https://www.instagram.com/nitinn_online_book?igsh=MWU0anU4a3Nka2F4YQ=="
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gradient-to-br from-pink-500 via-yellow-500 to-purple-500 rounded-full flex items-center justify-center hover:opacity-80 transition"
                    aria-label="Instagram"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5A4.25 4.25 0 0 0 20.5 16.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5zm4.25 3.25a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5zm0 1.5a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5zm5.25.75a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                    </svg>
                  </a>
                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/+917704070169"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition"
                    aria-label="WhatsApp"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z"/>
                    </svg>
                  </a>
                  {/* Telegram Group */}
                  <a
                    href="https://t.me/+wK_oOLQvUBo5ZWE9"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition"
                    aria-label="Telegram Group"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M9.04 16.62l-.39 3.47c.56 0 .8-.24 1.09-.53l2.62-2.5 5.44 3.97c1 .55 1.72.26 1.97-.92l3.58-16.74c.32-1.48-.54-2.06-1.5-1.7L2.18 9.3c-1.44.56-1.42 1.36-.25 1.72l4.37 1.37 10.13-6.39c.48-.3.92-.13.56.17l-8.2 7.41z"/>
                    </svg>
                  </a>
                  {/* Telegram Channel */}
                  <a
                    href="https://t.me/+1iAesH3uLk83YTA1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-400 rounded-full flex items-center justify-center hover:bg-blue-500 transition"
                    aria-label="Telegram Channel"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M9.04 16.62l-.39 3.47c.56 0 .8-.24 1.09-.53l2.62-2.5 5.44 3.97c1 .55 1.72.26 1.97-.92l3.58-16.74c.32-1.48-.54-2.06-1.5-1.7L2.18 9.3c-1.44.56-1.42 1.36-.25 1.72l4.37 1.37 10.13-6.39c.48-.3.92-.13.56.17l-8.2 7.41z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-bold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li><a href="/homepage-royal-welcome-hub" className="text-gray-400 hover:text-white transition-colors">Home</a></li>
                  <li><a href="/games-showcase-gaming-palace" className="text-gray-400 hover:text-white transition-colors">Games</a></li>
                  <li><a href="/success-stories-gallery-winner-showcase" className="text-gray-400 hover:text-white transition-colors">Winners</a></li>
                  <li><a href="/vip-contact-suite-whats-app-hub" className="text-gray-400 hover:text-white transition-colors">Contact</a></li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-lg font-bold mb-4">Contact Us</h4>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-green-500">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516z"/>
                    </svg>
                    <span className="text-gray-400">+917704070169</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-primary">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    <span className="text-gray-400">support@nitinnonlinehub.com</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-accent">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span className="text-gray-400">Mumbai, India</span>
                  </div>
                </div>
              </div>
            </div>

            {/* New: Related / Backlinks section */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div>
                <h5 className="text-sm font-semibold text-gray-300 mb-2">Explore Nitin Online Hub</h5>
                <ul className="text-gray-400 space-y-1">
                  <li><a href="/" className="hover:text-white">Nitin Online Hub</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Online Book</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Book</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Betting Book</a></li>
                  
                </ul>
              </div>
              <div>
                <h5 className="text-sm font-semibold text-gray-300 mb-2">Quick Access</h5>
                <ul className="text-gray-400 space-y-1">
                  <li><a href="/" className="hover:text-white">Nitin Online Book Login</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Online Book Sign Up</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Online Book App Download</a></li>
                </ul>
              </div>
              <div>
                <h5 className="text-sm font-semibold text-gray-300 mb-2">Learn More</h5>
                <ul className="text-gray-400 space-y-1">
                   <li><a href="/" className="hover:text-white">Nitin Online Book App</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Online Book India</a></li>
                  <li><a href="/" className="hover:text-white">Nitin Online Book Download</a></li>
                   <li><a href="/" className="hover:text-white">Nitin Online Book Review</a></li>
                </ul>
              </div>
              {/* ...existing footer columns if any ... */}
            </div>

            <div className="border-t border-gray-800 mt-8 pt-8 text-center">
              <p className="text-gray-400">
                © {new Date()?.getFullYear()} Nitin Online Hub. All rights reserved. | 
                <span className="text-primary"> Licensed & Regulated Gaming Platform</span>
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AboutDynastyBrandHeritage;